import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Heart, Clock, Euro } from "lucide-react";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";

interface Accompaniment {
  id: string;
  title: string;
  short_description: string;
  full_description: string | null;
  price: number;
  duration_minutes: number;
  image_url: string | null;
  is_active: boolean;
  display_order: number;
  booking_enabled: boolean;
  cabinet_booking_enabled: boolean;
  visio_booking_enabled: boolean;
  icon_name: string;
}

export default function AccompanimentsManagement() {
  const [accompaniments, setAccompaniments] = useState<Accompaniment[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccompaniment, setEditingAccompaniment] = useState<Accompaniment | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: "",
    short_description: "",
    full_description: "",
    price: 0,
    duration_minutes: 90,
    image_url: "",
    is_active: false,
    display_order: 0,
    booking_enabled: true,
    cabinet_booking_enabled: true,
    visio_booking_enabled: true,
    icon_name: "Heart",
  });

  useEffect(() => {
    fetchAccompaniments();
  }, []);

  const fetchAccompaniments = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("accompaniments")
        .select("*")
        .eq("user_id", user.id)
        .order("display_order");

      if (error) throw error;
      setAccompaniments(data || []);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les accompagnements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const accompanimentData = {
        title: formData.title,
        short_description: formData.short_description,
        full_description: formData.full_description || null,
        price: formData.price,
        duration_minutes: formData.duration_minutes,
        image_url: formData.image_url || null,
        is_active: formData.is_active,
        display_order: formData.display_order,
        booking_enabled: formData.booking_enabled,
        cabinet_booking_enabled: formData.cabinet_booking_enabled,
        visio_booking_enabled: formData.visio_booking_enabled,
        icon_name: formData.icon_name,
        user_id: user.id,
      };

      if (editingAccompaniment) {
        const { error } = await supabase
          .from("accompaniments")
          .update(accompanimentData)
          .eq("id", editingAccompaniment.id);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Accompagnement modifié avec succès",
        });
      } else {
        const { error } = await supabase.from("accompaniments").insert(accompanimentData);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Accompagnement créé avec succès",
        });
      }

      setDialogOpen(false);
      resetForm();
      fetchAccompaniments();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (accompaniment: Accompaniment) => {
    setEditingAccompaniment(accompaniment);
    setFormData({
      title: accompaniment.title,
      short_description: accompaniment.short_description,
      full_description: accompaniment.full_description || "",
      price: accompaniment.price,
      duration_minutes: accompaniment.duration_minutes,
      image_url: accompaniment.image_url || "",
      is_active: accompaniment.is_active,
      display_order: accompaniment.display_order,
      booking_enabled: accompaniment.booking_enabled,
      cabinet_booking_enabled: accompaniment.cabinet_booking_enabled,
      visio_booking_enabled: accompaniment.visio_booking_enabled,
      icon_name: accompaniment.icon_name,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet accompagnement ?")) return;

    try {
      const { error } = await supabase.from("accompaniments").delete().eq("id", id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Accompagnement supprimé",
      });
      fetchAccompaniments();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("accompaniments")
        .update({ is_active: !currentStatus })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: `Accompagnement ${!currentStatus ? "activé" : "désactivé"}`,
      });
      fetchAccompaniments();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      short_description: "",
      full_description: "",
      price: 0,
      duration_minutes: 90,
      image_url: "",
      is_active: false,
      display_order: 0,
      booking_enabled: true,
      cabinet_booking_enabled: true,
      visio_booking_enabled: true,
      icon_name: "Heart",
    });
    setEditingAccompaniment(null);
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center animate-pulse">
            <Heart className="w-6 h-6 text-accent" />
          </div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Accompagnements"
        subtitle="Gérez vos accompagnements personnalisés visibles sur votre site vitrine"
        icon={<Heart className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="accent"
        actions={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="gap-2 bg-gradient-primary">
                <Plus className="w-4 h-4" />
                Nouvel accompagnement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {editingAccompaniment ? "Modifier l'accompagnement" : "Nouvel accompagnement"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="short_description">Description courte *</Label>
                  <Textarea
                    id="short_description"
                    value={formData.short_description}
                    onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                    required
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="full_description">Description complète</Label>
                  <Textarea
                    id="full_description"
                    value={formData.full_description}
                    onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
                    rows={6}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price">Prix (€) *</Label>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duration_minutes">Durée (minutes) *</Label>
                    <Input
                      id="duration_minutes"
                      type="number"
                      min="15"
                      step="15"
                      value={formData.duration_minutes}
                      onChange={(e) =>
                        setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image_url">URL de l'image</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="display_order">Ordre d'affichage</Label>
                  <Input
                    id="display_order"
                    type="number"
                    min="0"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({ ...formData, display_order: parseInt(e.target.value) })
                    }
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">
                    Actif (visible sur le site vitrine)
                  </Label>
                </div>

                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold text-lg">Paramètres de réservation</h3>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="booking_enabled"
                      checked={formData.booking_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, booking_enabled: checked })}
                    />
                    <Label htmlFor="booking_enabled" className="cursor-pointer">
                      Activer les réservations
                    </Label>
                  </div>

                  {formData.booking_enabled && (
                    <>
                      <div className="flex items-center space-x-2 ml-6">
                        <Switch
                          id="cabinet_booking_enabled"
                          checked={formData.cabinet_booking_enabled}
                          onCheckedChange={(checked) => 
                            setFormData({ ...formData, cabinet_booking_enabled: checked })
                          }
                        />
                        <Label htmlFor="cabinet_booking_enabled" className="cursor-pointer">
                          Autoriser réservations au cabinet
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 ml-6">
                        <Switch
                          id="visio_booking_enabled"
                          checked={formData.visio_booking_enabled}
                          onCheckedChange={(checked) => 
                            setFormData({ ...formData, visio_booking_enabled: checked })
                          }
                        />
                        <Label htmlFor="visio_booking_enabled" className="cursor-pointer">
                          Autoriser réservations en visio
                        </Label>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Annuler
                  </Button>
                  <Button type="submit">
                    {editingAccompaniment ? "Modifier" : "Créer"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {accompaniments.length === 0 ? (
        <Card className="border-0 bg-card/80 backdrop-blur-sm shadow-card">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
              <Heart className="w-8 h-8 text-accent" />
            </div>
            <p className="text-muted-foreground mb-4">Aucun accompagnement pour le moment</p>
            <Button onClick={() => setDialogOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Créer mon premier accompagnement
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {accompaniments.map((accompaniment) => (
            <Card key={accompaniment.id} className="group overflow-hidden border-0 bg-card/80 backdrop-blur-sm shadow-card hover:shadow-glow transition-all duration-300 hover:-translate-y-1">
              {accompaniment.image_url && (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={accompaniment.image_url}
                    alt={accompaniment.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 w-12 h-12 bg-card/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                    <Heart className="w-6 h-6 text-accent" />
                  </div>
                </div>
              )}
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-lg group-hover:text-primary transition-colors">{accompaniment.title}</CardTitle>
                  <Switch
                    checked={accompaniment.is_active}
                    onCheckedChange={() => toggleActive(accompaniment.id, accompaniment.is_active)}
                  />
                </div>
                <CardDescription className="line-clamp-2">{accompaniment.short_description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                {accompaniment.full_description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {accompaniment.full_description}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-secondary font-semibold">
                    <Euro className="w-4 h-4" />
                    <span>{accompaniment.price}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                    <Clock className="w-4 h-4" />
                    <span>{accompaniment.duration_minutes} min</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm border-t border-border/30 pt-3">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs">Réservations:</span>
                    <Badge variant={accompaniment.booking_enabled ? "default" : "secondary"} className="text-xs">
                      {accompaniment.booking_enabled ? "Activées" : "Désactivées"}
                    </Badge>
                  </div>
                  {accompaniment.booking_enabled && (
                    <div className="flex gap-2 flex-wrap">
                      {accompaniment.cabinet_booking_enabled && (
                        <Badge variant="outline" className="text-xs border-border/50">
                          Cabinet
                        </Badge>
                      )}
                      {accompaniment.visio_booking_enabled && (
                        <Badge variant="outline" className="text-xs border-border/50">
                          Visio
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(accompaniment)}
                    className="flex-1 border-border/50"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(accompaniment.id)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
